import { useState, useRef } from 'react'
import { Button } from '@retailsync/ui'
import { DocumentArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { ImportSession } from '@retailsync/shared-types'
import type { ConnectorEngine } from '../../services/connector-engine'
import { MAX_FILE_SIZE, isSupportedFileName, describeFileType } from '../../services/file-parser'

interface FileUploadStepProps {
  session: ImportSession
  onUpdate: (updates: Partial<ImportSession>) => void
  engine: ConnectorEngine | null
  setIsProcessing: (v: boolean) => void
}

export function FileUploadStep({ session, onUpdate, engine, setIsProcessing }: FileUploadStepProps) {
  const [dragActive, setDragActive] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleFile = async (file: File) => {
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setParseError(`File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`)
      return
    }

    // Validate file type (extension based — MIME types differ per OS/browser).
    // Content is sniffed again inside the parser, so a CSV saved as .xlsx still works.
    if (!isSupportedFileName(file.name)) {
      setParseError('Please select a valid file format: CSV, Excel (.xlsx, .xls), or JSON')
      return
    }

    setParsing(true)
    setParseError(null)
    setIsProcessing(true)

    try {
      if (engine) {
        // Use the connector engine to parse the file locally
        const parsed = await engine.parseFile(file)

        // Store parsed data in session for downstream steps
        onUpdate({
          uploadedFile: file,
          rawData: parsed.rows,
        })
        // Also store uploadId-style identifier for backward compat
        onUpdate({
          uploadId: `local-${Date.now()}`,
        })

        console.log(`[FileUpload] Parsed ${parsed.totalRows} rows, ${parsed.headers.length} columns:`, parsed.headers)
      } else {
        // Fallback: just store the file, headers detected later
        onUpdate({
          uploadedFile: file,
          uploadId: `local-${Date.now()}`,
        })
      }
    } catch (error) {
      console.error('Parse error:', error)
      setParseError(error instanceof Error ? error.message : 'Failed to parse file')
    } finally {
      setParsing(false)
      setIsProcessing(false)
    }
  }

  const removeFile = () => {
    onUpdate({ uploadedFile: undefined, uploadId: undefined })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileTypeDisplay = (file: File) => {
    const type = file.type
    if (type === 'text/csv') return 'CSV'
    if (type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'Excel (XLSX)'
    if (type === 'application/vnd.ms-excel') return 'Excel (XLS)'
    if (type === 'application/json') return 'JSON'
    // Browsers often report an empty MIME type for .xlsx/.xls — fall back to the name
    return describeFileType(file.name.toLowerCase().endsWith('.json') ? 'json' :
      (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls') || file.name.toLowerCase().endsWith('.xlsm')) ? 'excel' : 'csv')
  }

  return (
    <div className="space-y-6">
      {/* File Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-blue-400 bg-blue-50'
            : session.uploadedFile
            ? 'border-green-400 bg-green-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {session.uploadedFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <DocumentArrowUpIcon className="h-12 w-12 text-green-600" />
            </div>
            <div>
              <p className="text-lg font-medium text-green-800">{session.uploadedFile.name}</p>
              <p className="text-sm text-green-600">
                {getFileTypeDisplay(session.uploadedFile)} • {formatFileSize(session.uploadedFile.size)}
              </p>
            </div>
            <Button variant="outline" onClick={removeFile}>
              <XMarkIcon className="h-4 w-4 mr-2" />
              Remove File
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <DocumentArrowUpIcon className="h-12 w-12 text-gray-400" />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">Drop your data file here</p>
              <p className="text-sm text-gray-500">
                or click to browse files (CSV, Excel, JSON)
              </p>
            </div>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={!!session.uploadedFile}
            >
              Choose File
            </Button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,.xlsm,.json"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Parse Progress / Error */}
      {parsing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <h3 className="text-sm font-medium text-blue-900">Parsing file…</h3>
          </div>
        </div>
      )}

      {parseError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-red-800 mb-1">Parse Error</h3>
          <p className="text-sm text-red-700">{parseError}</p>
        </div>
      )}

      {/* Parsed Data Summary */}
      {session.rawData && session.rawData.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-green-800 mb-1">File Parsed Successfully</h3>
          <p className="text-sm text-green-700">
            {session.rawData.length} rows detected • {session.rawData[0] ? Object.keys(session.rawData[0]).length : 0} columns
          </p>
        </div>
      )}

      {/* Supported Formats */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Supported Formats</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
          <div className="flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            CSV Files
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Excel (XLSX/XLS)
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            JSON Files
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Max 50MB
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Import Instructions</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Export your data from your current POS system</li>
          <li>• Ensure the file contains headers in the first row</li>
          <li>• Supported formats: CSV, Excel (.xlsx/.xls), JSON</li>
          <li>• Maximum file size: 50MB</li>
        </ul>
      </div>
    </div>
  )
}